import { Button, Input, Lock, Mail } from "@ews-admin/global-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { accountApi } from "../services/account.api";

const setPasswordSchema = (t: (key: string) => string) =>
  z
    .object({
      email: z
        .string()
        .nonempty(t("common.validation.required"))
        .email(t("common.validation.invalidEmail")),
      temporaryPassword: z
        .string()
        .nonempty(t("common.validation.required")),
      newPassword: z
        .string()
        .min(8, t("common.validation.passwordTooShort"))
        .regex(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          t("common.validation.passwordComplexity")
        ),
      confirm_password: z
        .string()
        .nonempty(t("common.validation.required")),
    })
    .refine((data) => data.newPassword === data.confirm_password, {
      message: t("common.validation.passwordMismatch"),
      path: ["confirm_password"],
    });

export const SetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(setPasswordSchema(t)),
  });

  const onSubmit = async (data: {
    email: string;
    temporaryPassword: string;
    newPassword: string;
    confirm_password: string;
  }) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await accountApi.setPasswordWithTemp({
        email: data.email,
        temporaryPassword: data.temporaryPassword,
        newPassword: data.newPassword,
      });

      // Redirect to login or show success message
      // The user will need to login with their new password
      navigate("/?passwordSet=true");
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === "object" && "message" in err
          ? (err.message as string)
          : t("setPassword.error", "Failed to set password. Please try again.");
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
        <div className="mb-6 text-center">
          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            {t("setPassword.title", "Set Your Password")}
          </h1>
          <p className="text-sm text-gray-600">
            {t(
              "setPassword.description",
              "Please enter your email, temporary password, and choose a new password."
            )}
          </p>
        </div>

        {error && (
          <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 border border-red-400 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            {...register("email")}
            type="email"
            label={t("setPassword.email", "Email")}
            leftIcon={<Mail size={16} />}
            error={errors.email?.message}
            required
          />

          <Input
            {...register("temporaryPassword")}
            type="password"
            label={t("setPassword.temporaryPassword", "Temporary Password")}
            leftIcon={<Lock size={16} />}
            showPasswordToggle
            error={errors.temporaryPassword?.message}
            required
          />

          <Input
            {...register("newPassword")}
            type="password"
            label={t("setPassword.newPassword", "New Password")}
            leftIcon={<Lock size={16} />}
            showPasswordToggle
            error={errors.newPassword?.message}
            required
          />

          <Input
            {...register("confirm_password")}
            type="password"
            label={t("setPassword.confirmPassword", "Confirm Password")}
            leftIcon={<Lock size={16} />}
            showPasswordToggle
            error={errors.confirm_password?.message}
            required
          />

          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            className="w-full"
          >
            {t("setPassword.submit", "Set Password")}
          </Button>
        </form>
      </div>
    </div>
  );
};

